import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Grid,
    Typography,
    Box,
    Chip,
    Divider,
    Avatar,
    Card,
    CardContent,
    CircularProgress
} from '@mui/material';
import { Close as CloseIcon, Business as BusinessIcon, Person as PersonIcon } from '@mui/icons-material';
import { Client, clientApi } from '../../services/clientApi';
import { useTheme } from '../../contexts/ThemeContext';

interface ClientViewProps {
    open: boolean;
    clientId: number | null;
    onClose: () => void;
}

const ClientView: React.FC<ClientViewProps> = ({ open, clientId, onClose }) => {
    const { theme } = useTheme();
    const [client, setClient] = useState<Client | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (open && clientId) {
            const fetchClient = async () => {
                setLoading(true);
                setError(null);
                try {
                    const data = await clientApi.getClient(clientId);
                    setClient(data);
                } catch (err: any) {
                    setError(err.message || 'Failed to fetch client details');
                } finally {
                    setLoading(false);
                }
            };
            fetchClient();
        } else if (!open) {
            setClient(null);
            setError(null);
        }
    }, [open, clientId]);

    if (!clientId) return null;

    // Helper to get client profile (API may return snake_case or camelCase)
    const getProfile = () => client?.clientProfile || client?.client_profile;

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusChip = (status: number) => {
        return status === 1 ? (
            <Chip
                label="Active"
                color="success"
                size="small"
                variant="outlined"
            />
        ) : (
            <Chip
                label="Inactive"
                color="error"
                size="small"
                variant="outlined"
            />
        );
    };

    return (
        <Dialog 
            open={open} 
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    bgcolor: theme.palette.background.paper,
                    borderRadius: 3,
                }
            }}
        >
            <DialogTitle sx={{
                pb: 1,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: `1px solid ${theme.palette.divider}`
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: theme.palette.secondary.main }}>
                        <PersonIcon />
                    </Avatar>
                    <Box>
                        <Typography variant="h6" sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>
                            {loading ? 'Loading...' : client?.name || 'Client Details'}
                        </Typography>
                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                            Client Details
                        </Typography>
                    </Box>
                </Box>
                <Button
                    onClick={onClose}
                    sx={{ 
                        color: theme.palette.text.secondary,
                        minWidth: 'auto',
                        p: 1
                    }}
                >
                    <CloseIcon />
                </Button>
            </DialogTitle>

            <DialogContent sx={{ pt: 3 }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                        <Typography color="error">{error}</Typography>
                    </Box>
                ) : !client ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                        <Typography>No client data available</Typography>
                    </Box>
                ) : (
                <Grid container spacing={3}>
                    {/* Basic Information */}
                    <Grid size={{ xs: 12 }}>
                        <Typography variant="h6" sx={{ mb: 2, color: theme.palette.text.primary, fontWeight: 600 }}>
                            Basic Information
                        </Typography>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Card sx={{ bgcolor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}` }}>
                            <CardContent sx={{ p: 2 }}>
                                <Typography variant="caption" color="textSecondary" display="block">
                                    Full Name
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                                    {client.name}
                                </Typography>

                                <Typography variant="caption" color="textSecondary" display="block">
                                    Email
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    {client.email}
                                </Typography>

                                <Typography variant="caption" color="textSecondary" display="block">
                                    Login ID
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    {client.loginID}
                                </Typography>

                                <Typography variant="caption" color="textSecondary" display="block">
                                    Status
                                </Typography>
                                <Box sx={{ mt: 0.5 }}>
                                    {getStatusChip(client.status)}
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Card sx={{ bgcolor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}` }}>
                            <CardContent sx={{ p: 2 }}>
                                <Typography variant="caption" color="textSecondary" display="block">
                                    Branch
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    {client.branch?.name || 'Not assigned'}
                                </Typography>

                                <Typography variant="caption" color="textSecondary" display="block">
                                    Created Date
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    {formatDate(client.created_at)}
                                </Typography>

                                <Typography variant="caption" color="textSecondary" display="block">
                                    Account Type
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    {getProfile()?.account_type || client.type}
                                </Typography>

                                <Typography variant="caption" color="textSecondary" display="block">
                                    Account Number
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    {getProfile()?.account_no || 'Not assigned'}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Authorized Person Information */}
                    <Grid size={{ xs: 12 }}>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="h6" sx={{ mb: 2, color: theme.palette.text.primary, fontWeight: 600 }}>
                            Authorized Person
                        </Typography>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Card sx={{ bgcolor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}` }}>
                            <CardContent sx={{ p: 2 }}>
                                <Typography variant="caption" color="textSecondary" display="block">
                                    Name
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    {getProfile()?.authorized_person_name || 'Not provided'}
                                </Typography>

                                <Typography variant="caption" color="textSecondary" display="block">
                                    Designation
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    {getProfile()?.authorized_designation || 'Not provided'}
                                </Typography>

                                <Typography variant="caption" color="textSecondary" display="block">
                                    Phone
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    {getProfile()?.authorized_phone || 'Not provided'}
                                </Typography>

                                <Typography variant="caption" color="textSecondary" display="block">
                                    CNIC
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    {getProfile()?.authorized_cnic || 'Not provided'}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Card sx={{ bgcolor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}` }}>
                            <CardContent sx={{ p: 2 }}>
                                <Typography variant="caption" color="textSecondary" display="block">
                                    Admin Contact Name
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    {getProfile()?.admin_contact_name || 'Not provided'}
                                </Typography>

                                <Typography variant="caption" color="textSecondary" display="block">
                                    Admin Phone
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    {getProfile()?.admin_phone || 'Not provided'}
                                </Typography>

                                <Typography variant="caption" color="textSecondary" display="block">
                                    Finance Contact Name
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    {getProfile()?.finance_contact_name || 'Not provided'}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Location Information */}
                    <Grid size={{ xs: 12 }}>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="h6" sx={{ mb: 2, color: theme.palette.text.primary, fontWeight: 600 }}>
                            Location Information
                        </Typography>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Card sx={{ bgcolor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}` }}>
                            <CardContent sx={{ p: 2 }}>
                                <Typography variant="caption" color="textSecondary" display="block">
                                    Area Zone
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    {getProfile()?.areaZone?.name || (typeof getProfile()?.area_zone === 'object' ? (getProfile()?.area_zone as any)?.name : null) || 'Not specified'}
                                </Typography>

                                <Typography variant="caption" color="textSecondary" display="block">
                                    Point of Sale
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    {getProfile()?.pointOfSale?.name || (typeof getProfile()?.point_of_sale === 'object' ? (getProfile()?.point_of_sale as any)?.name : null) || 'Not specified'}
                                </Typography>

                                <Typography variant="caption" color="textSecondary" display="block">
                                    Address
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    {getProfile()?.address || 'Not provided'}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Card sx={{ bgcolor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}` }}>
                            <CardContent sx={{ p: 2 }}>
                                <Typography variant="caption" color="textSecondary" display="block">
                                    Pickup Address
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    {getProfile()?.pickup_address || 'Not provided'}
                                </Typography>

                                <Typography variant="caption" color="textSecondary" display="block">
                                    Pickup Contact Person
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    {getProfile()?.pickup_contact_person || 'Not provided'}
                                </Typography>

                                <Typography variant="caption" color="textSecondary" display="block">
                                    Pickup Phone
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    {getProfile()?.pickup_phone || 'Not provided'}
                                </Typography>

                                <Typography variant="caption" color="textSecondary" display="block">
                                    Pickup Email
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    {getProfile()?.pickup_email || 'Not provided'}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Company Information */}
                    <Grid size={{ xs: 12 }}>
                        <Divider sx={{ my: 2 }} />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <BusinessIcon sx={{ color: theme.palette.secondary.main }} />
                            <Typography variant="h6" sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>
                                Company Information
                            </Typography>
                        </Box>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Card sx={{ bgcolor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}` }}>
                            <CardContent sx={{ p: 2 }}>
                                <Typography variant="caption" color="textSecondary" display="block">
                                    Company Name
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    {getProfile()?.company_name || 'Not provided'}
                                </Typography>

                                <Typography variant="caption" color="textSecondary" display="block">
                                    Brand Name
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    {getProfile()?.brand_name || 'Not provided'}
                                </Typography>

                                <Typography variant="caption" color="textSecondary" display="block">
                                    Web Address
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    {getProfile()?.web_address || 'Not provided'}
                                </Typography>

                                <Typography variant="caption" color="textSecondary" display="block">
                                    Business Sector
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    {getProfile()?.business_sector || 'Not provided'}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Card sx={{ bgcolor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}` }}>
                            <CardContent sx={{ p: 2 }}>
                                <Typography variant="caption" color="textSecondary" display="block">
                                    Business Registration Status
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    {getProfile()?.business_registration_status || 'Not provided'}
                                </Typography>

                                <Typography variant="caption" color="textSecondary" display="block">
                                    NTN (National Tax Number)
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    {getProfile()?.ntn || 'Not provided'}
                                </Typography>

                                <Typography variant="caption" color="textSecondary" display="block">
                                    GST (Goods and Services Tax)
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    {getProfile()?.gst || 'Not provided'}
                                </Typography>

                                <Typography variant="caption" color="textSecondary" display="block">
                                    Products
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    {getProfile()?.products || 'Not provided'}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
                )}
            </DialogContent>

            <DialogActions sx={{ 
                p: 3, 
                pt: 2,
                borderTop: `1px solid ${theme.palette.divider}`
            }}>
                <Button
                    onClick={onClose}
                    variant="outlined"
                    sx={{
                        borderRadius: 2,
                        borderColor: theme.palette.divider,
                        color: theme.palette.text.secondary,
                        '&:hover': {
                            borderColor: theme.palette.text.secondary,
                        }
                    }}
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ClientView;
